import React, { useState } from 'react';
import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import { Snackbar } from '@shared/components/Snackbar';
import { SHARED_MESSAGES } from '@/shared/constants/messages';
import FilePDFIcon from '@/assets/svg/FilePDF.svg';
import FileJPEGIcon from '@/assets/svg/FileJPEG.svg';
import FileJPGIcon from '@/assets/svg/FileJPG.svg';
import FilePNGIcon from '@/assets/svg/FilePNG.svg';
import FileDOCXIcon from '@/assets/svg/FileDOCX.svg';
import { VIEW_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';
import type {
  ServiceRequestImage,
  ViewServiceRequestDetail,
} from '../../types/viewServiceRequestType';
import { SectionCard } from './viewServiceRequestUI';

const getFileIcon = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpeg':
      return <FileJPEGIcon className="w-9 h-9 shrink-0" />;
    case 'jpg':
      return <FileJPGIcon className="w-9 h-9 shrink-0" />;
    case 'png':
      return <FilePNGIcon className="w-9 h-9 shrink-0" />;
    case 'docx':
      return <FileDOCXIcon className="w-9 h-9 shrink-0" />;
    default:
      return <FilePDFIcon className="w-9 h-9 shrink-0" />;
  }
};

const openInNewTab = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const downloadFile = async (url: string, filename: string): Promise<void> => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      return;
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    openInNewTab(url);
  }
};

interface FileRowProps {
  file: ServiceRequestImage;
  testId: string;
  onDownload: (url: string, filename: string) => void;
}

const FileRow: React.FC<FileRowProps> = ({ file, testId, onDownload }) => (
  <div className="flex items-center justify-between gap-3" data-testid={testId}>
    <div className="flex items-center gap-3 min-w-0">
      <button
        type="button"
        onClick={() => openInNewTab(file.url)}
        className="group relative shrink-0 p-0 border-0 bg-transparent cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
        aria-label={`Otwórz ${file.filename}`}
        data-testid={`${testId}-view`}
      >
        {getFileIcon(file.filename)}
        <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity rounded">
          <EyeIcon className="w-5 h-5 text-white" />
        </span>
      </button>
      <span className="text-sm text-[#181A1B] break-all">{file.filename}</span>
    </div>
    <div className="flex items-center gap-1 shrink-0">
      {/* Tablet (and below) only: explicit view action, since touch devices
          have no hover to reveal the eye overlay on the file thumbnail.
          Hidden on desktop (lg+), where the thumbnail overlay handles view. */}
      <button
        type="button"
        onClick={() => openInNewTab(file.url)}
        className="lg:hidden p-2 rounded-full cursor-pointer hover:bg-[#F3F4F6] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] text-[#181A1B]"
        aria-label={`Otwórz ${file.filename}`}
        data-testid={`${testId}-view-tablet`}
      >
        <EyeIcon className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={() => onDownload(file.url, file.filename)}
        className="p-2 rounded-full cursor-pointer hover:bg-[#F3F4F6] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] text-[#181A1B]"
        aria-label={`Pobierz ${file.filename}`}
        data-testid={`${testId}-download`}
      >
        <ArrowDownTrayIcon className="w-5 h-5" />
      </button>
    </div>
  </div>
);

interface SubLabelProps {
  text: string;
}

const SubLabel: React.FC<SubLabelProps> = ({ text }) => (
  <p className="text-bodySmall font-normal text-on-surface-variant">{text}</p>
);

interface SRDetailsAttachmentsTabProps {
  detail: ViewServiceRequestDetail | undefined;
}

const SRDetailsAttachmentsTab: React.FC<SRDetailsAttachmentsTabProps> = ({
  detail,
}) => {
  const images = detail?.images ?? [];
  const document = detail?.document ?? null;
  const description = detail?.description?.trim() ?? '';
  const [showDownloadSnackbar, setShowDownloadSnackbar] = useState(false);

  const handleDownload = (url: string, filename: string): void => {
    setShowDownloadSnackbar(true);
    downloadFile(url, filename);
  };

  return (
    <>
      <SectionCard
        title={L.NAV_DETAILS}
        testId="view-sr-section-details-attachments"
      >
        <div className="flex flex-col gap-2">
          <SubLabel text={L.SECTION_DESCRIPTION} />
          <p
            className="text-sm text-[#181A1B] whitespace-pre-wrap break-words"
            data-testid="view-sr-description-value"
          >
            {description.length > 0 ? description : L.EMPTY_CELL}
          </p>
        </div>

        {images.length > 0 && (
          <div className="flex flex-col gap-3">
            <SubLabel text={L.SECTION_IMAGES} />
            <div className="flex flex-col gap-4">
              {images.map((img, idx) => (
                <FileRow
                  key={`${img.filename}-${idx}`}
                  file={img}
                  testId={`view-sr-image-${idx}`}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          </div>
        )}

        {document && (
          <div className="flex flex-col gap-3">
            <SubLabel text={L.SECTION_DOCUMENT} />
            <FileRow
              file={document}
              testId="view-sr-document"
              onDownload={handleDownload}
            />
          </div>
        )}
      </SectionCard>

      <Snackbar
        open={showDownloadSnackbar}
        message={SHARED_MESSAGES.DOWNLOAD_READY_MESSAGE}
        variant="success"
        onClose={() => setShowDownloadSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
    </>
  );
};

export default SRDetailsAttachmentsTab;
